import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';

// Keep the same interface for compatibility
interface GooglePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface LocationSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

// Foursquare configuration
const FOURSQUARE_CONFIG = {
  apiKey:import.meta.env.VITE_FOURSQUARE_API_KEY, // Replace with your API key
  baseUrl: 'https://api.foursquare.com/v3'
};

const textStyles = {
  // Base text styles
  base: {
    fontFamily: 'var(--chat-font-family)',
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-normal)',
    lineHeight: 'var(--chat-line-height)',
    color: 'var(--planner-text-primary)'
  },
  // Header styles
  h2: {
    fontSize: 'var(--planner-heading-size)',
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  h3: {
    fontSize: 'var(--planner-subheading-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  h4: {
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  // Text sizes
  large: {
    fontSize: 'var(--planner-subheading-size)'
  },
  small: {
    fontSize: 'var(--planner-small-size)'
  },
  xs: {
    fontSize: 'calc(var(--chat-font-size) * 0.833)' // 10px if base is 12px
  },
  // Font weights
  medium: {
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  semibold: {
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  // Colors
  secondary: {
    color: 'var(--planner-text-secondary)'
  },
  tertiary: {
    color: 'var(--planner-text-tertiary)'
  }
};

export function LocationSearch({ value, onChange, placeholder = 'Where to?' }: LocationSearchProps) {
  const [predictions, setPredictions] = useState<GooglePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  

  // Foursquare doesn't need session tokens, but we'll keep a request ID for consistency
  const requestIdRef = useRef<string>(Date.now().toString());

  // Keep input in sync with external value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  //console.log('LocationSearch initialized with value:', FOURSQUARE_CONFIG);
  console.log('Foursquare API Key:', value, onChange, placeholder);

  console.log('Foursquare API Key:', FOURSQUARE_CONFIG.apiKey);


  const getPlacePredictions = async (input: string) => {
    if (!FOURSQUARE_CONFIG.apiKey || input.length < 2) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);

    try {
      // Foursquare autocomplete API
      const response = await fetch(
        `${FOURSQUARE_CONFIG.baseUrl}/autocomplete?` +
        `query=${encodeURIComponent(input)}&` +
        `types=geo&` + // geo type includes cities, regions, countries
        `limit=5&` +
        `locale=en`, // language setting
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': FOURSQUARE_CONFIG.apiKey
          }
        }
      );

      setIsLoading(false);

      if (!response.ok) {
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        // Map Foursquare results to match Google's format
        const mappedPredictions: GooglePrediction[] = data.results
          .filter((result: any) => result.type === 'geo' || result.type === 'place')
          .map((result: any) => {
            // Extract main text and secondary text from Foursquare data
            const parts = result.text?.primary?.split(',') || [result.text?.primary || ''];
            const mainText = parts[0].trim();
            const secondaryText = parts.slice(1).join(',').trim() || 
                                result.place?.location?.formatted_address || 
                                result.geo?.name || 
                                '';

            return {
              description: result.text?.primary || '',
              place_id: result.id || result.link || Date.now().toString(),
              structured_formatting: {
                main_text: mainText,
                secondary_text: secondaryText
              }
            };
          });

        setPredictions(mappedPredictions);
        setIsOpen(true);
      } else {
        setPredictions([]);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Foursquare autocomplete error:', error);
      setIsLoading(false);
      setPredictions([]);
      setIsOpen(false);
    }
  };

  // Debounce function to limit API calls
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };
  
  // Create debounced version of getPlacePredictions
  const debouncedGetPredictions = useRef(
    debounce((input: string) => getPlacePredictions(input), 300)
  ).current;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (newValue.length >= 2) {
      debouncedGetPredictions(newValue);
    } else {
      setPredictions([]);
      setIsOpen(false);
    }
  };

  const handlePredictionSelect = (prediction: GooglePrediction) => {
    setInputValue(prediction.description);
    onChange(prediction.description);
    setIsOpen(false);
    setPredictions([]);
    
    // Generate new request ID after selection (mimicking session token behavior)
    requestIdRef.current = Date.now().toString();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && predictions.length > 0) {
      e.preventDefault();
      handlePredictionSelect(predictions[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current && 
        dropdownRef.current && 
        !inputRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle form submission
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onChange(inputValue);
  }

  return (
    <form onSubmit={handleFormSubmit} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 2 && debouncedGetPredictions(inputValue)}
          placeholder={placeholder}
          className="w-full px-4 py-2 pl-8 border border-gray-300 rounded-full shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          autoComplete="off"
          style={{
            ...textStyles.base,
            ...textStyles.medium
          }}
        />
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          style={{ 
            width: 'var(--planner-icon-size-sm)', 
            height: 'var(--planner-icon-size-sm)' 
          }}
        />
      </div>

      {/* Predictions dropdown */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
        >
          {isLoading ? (
            <div 
              className="px-4 py-3 text-center"
              style={{
                ...textStyles.base,
                ...textStyles.small,
                ...textStyles.secondary
              }}
            >
              <div 
                className="inline-block h-4 w-4 border-2 border-t-transparent border-gray-300 rounded-full animate-spin mr-2"
                style={{
                  width: 'var(--planner-icon-size-sm)',
                  height: 'var(--planner-icon-size-sm)'
                }}
              />
              Loading suggestions...
            </div>
          ) : predictions.length > 0 ? (
            <ul className="py-1 divide-y divide-gray-100">
              {predictions.map((prediction) => (
                <li
                  key={prediction.place_id}
                  onClick={() => handlePredictionSelect(prediction)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex flex-col gap-1">
                    <div 
                      className="font-medium"
                      style={{
                        ...textStyles.base,
                        ...textStyles.medium,
                        ...textStyles.medium
                      }}
                    >
                      {prediction.structured_formatting.main_text}
                    </div>
                    <div 
                      style={{
                        ...textStyles.base,
                        ...textStyles.small,
                        ...textStyles.secondary
                      }}
                    >
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : inputValue.length >= 2 ? (
            <div 
              className="px-4 py-3 text-center"
              style={{
                ...textStyles.base,
                ...textStyles.small,
                ...textStyles.secondary
              }}
            >
              No results found
            </div>
          ) : null}
        </div>
      )}
    </form>
  );
}
/* 

const request = {
  input,
  sessionToken: sessionToken.current,
  types: ['(cities)', '(regions)', 'country', 'locality', 'natural_feature', 'point_of_interest'],
  , // restrict to cities
  language: 'en', // language setting
};

accounting
airport
amusement_park
aquarium
art_gallery
atm
bakery
bank
bar
beauty_salon
bicycle_store
book_store
bowling_alley
bus_station
cafe
campground
car_dealer
car_rental
car_repair
car_wash
casino
cemetery
church
city_hall
clothing_store
convenience_store
courthouse
dentist
department_store
doctor
drugstore
electrician
electronics_store
embassy
fire_station
florist
funeral_home
furniture_store
gas_station
gym
hair_care
hardware_store
hindu_temple
home_goods_store
hospital
insurance_agency
jewelry_store
laundry
lawyer
library
light_rail_station
liquor_store
local_government_office
locksmith
lodging
meal_delivery
meal_takeaway
mosque
movie_rental
movie_theater
moving_company
museum
night_club
painter
park
parking
pet_store
pharmacy
physiotherapist
plumber
police
post_office
primary_school
real_estate_agency
restaurant
roofing_contractor
rv_park
school
secondary_school
shoe_store
shopping_mall
spa
stadium
storage
store
subway_station
supermarket
synagogue
taxi_stand
tourist_attraction
train_station
transit_station
travel_agency
university
veterinary_care
zoo */